import { createContext, useEffect, useReducer, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

// third-party
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import IdleWarningDialog from 'components/IdleWarningDialog';
import axios, { scheduleProactiveRefresh, cancelProactiveRefresh, PARKED_REFRESH_KEY } from 'utils/axios';
import { startImpersonation, stopImpersonation } from 'services/api/impersonation';
import * as authService from 'services/authService';
import { getDeviceFingerprint } from 'utils/deviceFingerprint';
import useIdleTimer from 'hooks/useIdleTimer';
import { AUTH_SESSION_EVENT } from 'hooks/useOrgBranding';
import {
  cacheDropdownValues,
  saveTherapistProfile,
  cacheChildrenForTherapist,
  cacheSessionsForTherapist,
  cacheClinicalData,
  clearSyncedEntriesForTherapist,
  clearSyncedSessionUpdatesForTherapist
} from 'utils/offlineDB';
import useOnlineStatus from 'hooks/useOnlineStatus';
import { listClients } from 'services/api/clients';
import { listSessions } from 'services/api/sessions';
import { fetchClinicalBundle, clinicalBundleToCache } from 'services/clinicalBundle';
import { transformClient, transformSession } from 'utils/apiTransformers';
// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  try {
    const decoded = jwtDecode(serviceToken);
    return decoded.exp > Date.now() / 1000;
  } catch (error) {
    console.error('Token verification error:', error);
    return false;
  }
};

const setSession = (serviceToken, userData = null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  } else {
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('userData');
    delete axios.defaults.headers.common.Authorization;
  }
  // ThemeCustomization sits ABOVE this provider in the tree, so it cannot read
  // auth state through context. It listens for this event instead, to pick up
  // the signed-in organization's branding on login and drop it on logout. The
  // single choke point for both means neither path can forget to fire it.
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EVENT, { detail: { signedIn: Boolean(serviceToken) } }));
};

/* The API's snake_case user turned into the camelCase shape this app stores and
 * renders. Login, MFA verification, Duo enrolment and BOTH ends of impersonation
 * all put a user into the session, and every one of them has to agree — a route
 * guard reading `role` off a differently-built object is how someone ends up
 * with the wrong menu tree. One mapping, five callers. */
const toSessionUser = (user) => ({
  // Core identity
  id: user.id,
  person_id: user.person_id || null,
  email: user.email,
  username: user.username,
  // Name fields
  name: user.display_name || (user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.username),
  firstName: user.first_name,
  lastName: user.last_name,
  displayName: user.display_name,
  // Role and permissions
  role: user.role || (user.is_superuser ? 'admin' : 'user'),
  roles: user.roles || [user.role || 'user'],
  permissions: user.permissions || [],
  isSuperuser: user.is_superuser || false,
  // Organization context
  orgId: user.org_id,
  orgName: user.org_name,
  centerId: user.center_id,
  centerName: user.center_name,
  // Profile
  avatar: user.profile_image_url || user.avatar,
  phone: user.phone,
  // Status
  status: user.status,
  mfaEnabled: user.mfa_enabled || false,
  lastLoginAt: user.last_login_at
});

/* IMPERSONATION.
 *
 * The impersonation token is an ordinary bearer token as far as the app is
 * concerned — only its claims differ — so it goes through the same setSession
 * choke point and everything downstream keeps working unchanged.
 *
 * Whether a session is impersonating is read from the TOKEN, not from React
 * state, so a page reload cannot strand someone as another user with no way
 * back: the banner and its Exit button reappear on their own.
 *
 * The actor's refresh token is PARKED under a separate key for the duration.
 * Left in place, a proactive refresh would quietly swap the actor's own token
 * back in mid-session and end the impersonation without anyone asking; removed
 * outright, the actor would have no renewable session to come back to.
 *
 * The key itself lives in utils/axios, because the 401 handler there needs it
 * too: an impersonation token that simply expires must put the actor back in
 * their own session rather than at the login screen. */

const readImpersonation = (serviceToken) => {
  if (!serviceToken) return { isImpersonating: false, impersonatorEmail: null };
  try {
    const decoded = jwtDecode(serviceToken);
    return decoded?.imp
      ? { isImpersonating: true, impersonatorEmail: decoded.act_email || null }
      : { isImpersonating: false, impersonatorEmail: null };
  } catch {
    return { isImpersonating: false, impersonatorEmail: null };
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

// Rolling-timeout configuration. Reads from Vite env so it can be tuned per
// environment without a code change, but defaults align with the backend
// SESSION_IDLE_TIMEOUT_MINUTES so the two stay in sync out of the box.
const IDLE_TIMEOUT_MINUTES = Number(import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MINUTES) || 15;
const IDLE_WARNING_SECONDS = Number(import.meta.env.VITE_SESSION_IDLE_WARNING_SECONDS) || 60;

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { clearOfflineSession, clearActiveOfflineUser, setOfflineUser, isOnline } = useOnlineStatus();
  const deviceFingerprintRef = useRef(null);
  const rememberDeviceRef = useRef(false);

  // Generate device fingerprint on mount
  useEffect(() => {
    const generateFingerprint = async () => {
      try {
        deviceFingerprintRef.current = await getDeviceFingerprint();
      } catch (error) {
        console.error('Failed to generate device fingerprint:', error);
      }
    };
    generateFingerprint();
  }, []);

  /**
   * Cache data for offline use and sync any pending offline entries
   * Called after successful login
   */
  const handlePostLoginOfflineTasks = useCallback(
    async (user) => {
      // Offline features are only for therapists — skip all offline tasks for other roles
      // (inclusionhead, supervisor, admin don't use offline data entry)
      if (user.role !== 'therapist') {
        return;
      }

      try {
        // Clean up old synced entries from previous sessions to prevent IndexedDB bloat
        try {
          await clearSyncedEntriesForTherapist(user.id);
          await clearSyncedSessionUpdatesForTherapist(user.id);
        } catch (cleanupErr) {
          console.warn('Failed to clear old synced entries:', cleanupErr);
        }

        // Save user session for offline reference — updates both IndexedDB AND React state
        // so OfflineCalendar/OfflineDataEntry skip the profile picker for logged-in users
        await setOfflineUser(user);
        // Save/update therapist profile for offline use
        await saveTherapistProfile(user);
        // Cache therapist data for offline use
        try {
          const personId = user.person_id || user.id;

          // Cache children scoped to this therapist
          const clientsRes = await listClients({ therapist_id: personId, is_active: true }).catch((err) => {
            console.warn(`Failed to fetch clients for therapist ${personId}:`, err.message);
            return null;
          });
          const assignedChildren = clientsRes ? (clientsRes.items || clientsRes || []).map(transformClient) : [];
          if (assignedChildren.length > 0) {
            await cacheChildrenForTherapist(assignedChildren, personId);
          }

          // Cache sessions scoped to this therapist (broad date range for offline use)
          const now = new Date();
          const cacheStart = new Date(now);
          cacheStart.setDate(cacheStart.getDate() - 14); // 2 weeks back
          const cacheEnd = new Date(now);
          cacheEnd.setDate(cacheEnd.getDate() + 30); // 1 month forward
          const formatDate = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

          const sessionsRes = await listSessions({
            therapist_id: personId,
            start_date: formatDate(cacheStart),
            end_date: formatDate(cacheEnd),
            page_size: 500
          }).catch(() => null);
          const sessions = sessionsRes ? (sessionsRes.items || sessionsRes || []).map(transformSession) : [];

          if (sessions.length > 0) {
            await cacheSessionsForTherapist(sessions, personId);
          }

          // Cache the FULL clinical bundle per child (IEP snapshot, targets
          // overlay with names, IEP-tracked behaviors, mand items, maintenance
          // history) — the same builder the data-collection page uses. Running
          // it on every login refreshes the offline cache with the latest
          // supervisor edits, so offline shows correct behaviors/target names
          // without the therapist having to open each child online first.
          for (const child of assignedChildren) {
            try {
              const bundle = await fetchClinicalBundle(child.id);
              await cacheClinicalData(child.id, clinicalBundleToCache(bundle));
            } catch (childErr) {
              console.warn(`Failed to cache clinical data for child ${child.id}:`, childErr.message);
            }
          }
          // Cache common dropdown values (shared across therapists)
          const dropdownsToCache = {
            behaviors: ['Aggression', 'Self-Injury', 'Elopement', 'Non-compliance', 'Screaming', 'Property Destruction'],
            antecedents: ['Demand placed', 'Transition', 'Denied access', 'No attention', 'Task change', 'Environmental change', 'Other'],
            consequences: ['Attention given', 'Task removed', 'Access given', 'Ignored', 'Redirected', 'Time-out', 'Other'],
            promptLevels: ['Full Physical', 'Partial Physical', 'Model', 'Gestural', 'Verbal', 'Independent']
          };

          for (const [key, values] of Object.entries(dropdownsToCache)) {
            await cacheDropdownValues(key, values);
          }
        } catch (error) {
          console.error('Failed to cache therapist data for offline:', error);
        }
      } catch (error) {
        console.error('Post-login offline tasks error:', error);
      }
    },
    [setOfflineUser]
  );

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        const storedUserData = window.localStorage.getItem('userData');

        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);

          let user;

          // Always decode the JWT so we have the authoritative org_id / center_id.
          let decoded;
          try {
            decoded = jwtDecode(serviceToken);
          } catch {
            /* invalid token — handled below */
          }

          // Try to get user data from localStorage first (preserves role / display name)
          if (storedUserData) {
            try {
              user = JSON.parse(storedUserData);
              // Backfill org/center from the current JWT so stale sessions
              // that pre-date these fields being stored still work correctly.
              if (decoded) {
                if (!user.orgId && decoded.org_id) user = { ...user, orgId: decoded.org_id };
                if (!user.centerId && decoded.center_id) user = { ...user, centerId: decoded.center_id };
              }
            } catch (e) {
              console.error('Error parsing stored user data:', e);
            }
          }

          // Fallback to decoding token if no stored user data
          if (!user && decoded) {
            user = {
              id: decoded.sub,
              email: decoded.email,
              username: decoded.username,
              name: decoded.username || decoded.email?.split('@')[0],
              person_id: decoded.person_id || null,
              role: decoded.role || 'user',
              roles: decoded.role ? [decoded.role] : ['user'],
              orgId: decoded.org_id,
              centerId: decoded.center_id
            };
          }

          /* PERSON ID MUST SURVIVE A RELOAD.
           *
           * Clinical ownership is recorded against the PERSON, not the login:
           * client_session.started_by, therapist assignments and caseload scoping are
           * all person ids. Restoring a session without one leaves the viewer unable to
           * prove they are the person running a session, and every runner-gated control
           * — Collect Data, End Session — goes dead with a tooltip blaming somebody
           * else. That is the bug a supervisor hits by starting a session, leaving, and
           * coming back.
           *
           * Recovered in three steps, cheapest first: the stored profile, then the
           * token's own claim (present on tokens issued after build_token_data started
           * carrying it), and only if both are silent, one call to /auth/me — which is
           * authoritative and also repairs a token minted before the claim existed. The
           * repaired profile is written back so the round trip happens once, not on
           * every reload.
           */
          if (user && !user.person_id) {
            if (decoded?.person_id) {
              user = { ...user, person_id: decoded.person_id };
            } else {
              try {
                const me = await axios.get('/api/v1/auth/me');
                if (me?.data?.person_id) user = { ...user, person_id: me.data.person_id };
              } catch (err) {
                // Non-fatal: the session is still valid and everything that does not
                // turn on person identity keeps working.
                console.warn('Could not recover person_id on session restore:', err?.message);
              }
            }
            if (user.person_id) localStorage.setItem('userData', JSON.stringify(user));
          }

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
          // Re-establish the offline session on reload so a logged-in therapist
          // who later goes offline is recognized (and skips the profile picker).
          // Lightweight: just the session + profile — full data caching already
          // ran at login and is read back from IndexedDB by the offline pages.
          if (user?.role === 'therapist') {
            setOfflineUser(user);
            saveTherapistProfile(user).catch((err) => console.warn('Failed to refresh offline profile on restore:', err.message));
          }
          // Token rehydrated on app load — kick off the proactive refresh
          // timer so the session stays alive past the 60-min access TTL
          // without ever surfacing a 401 to the user.
          scheduleProactiveRefresh();
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error('Init error:', err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
    // Run once on mount to rehydrate the session; offline helpers are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Login with email and password
   * Returns MFA info if MFA is required, or completes login if not
   */
  const login = useCallback(
    async (email, password, recaptchaToken = null, rememberDevice = false) => {
      try {
        // Store remember device preference for MFA verification
        rememberDeviceRef.current = rememberDevice;

        const response = await authService.login(email, password, recaptchaToken, deviceFingerprintRef.current, rememberDevice);

        if (response.requires_mfa) {
          // MFA is required - return MFA state for component to handle
          return {
            requiresMfa: true,
            mfaToken: response.mfa_token,
            mfaMethods: response.available_mfa_methods || [],
            mfaMethod: response.mfa_method
          };
        }

        // No MFA required - complete login
        const { access_token, refresh_token, user } = response;

        // Build user object with new security schema fields
        const userWithRole = toSessionUser(user);

        setSession(access_token, userWithRole);
        localStorage.setItem('refreshToken', refresh_token);

        // flushSync forces the LOGIN reducer update to commit before this
        // function returns. Without it, the caller's follow-up navigate()
        // can mount the new route's AuthGuard / RoleGuard while they still
        // see isLoggedIn=false, kicking the user back to /login.
        flushSync(() => {
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: userWithRole
            }
          });
        });

        // Kick off the proactive refresh loop now that we have a fresh
        // access token. Keeps the session alive past 60 min without the
        // user ever seeing a 401.
        scheduleProactiveRefresh();

        // Cache data for offline use and sync pending entries
        handlePostLoginOfflineTasks(userWithRole);

        // Return the user so the caller can drive an immediate role-based
        // redirect without waiting for the context state to propagate.
        return { requiresMfa: false, user: userWithRole };
      } catch (error) {
        console.error('Login error:', error);
        throw new Error(error.detail || error.message || 'Login failed');
      }
    },
    [handlePostLoginOfflineTasks]
  );

  /**
   * Trigger MFA method (send OTP or initiate push)
   */
  const triggerMFA = useCallback(async (mfaToken, method) => {
    try {
      const response = await authService.triggerMFA(mfaToken, method);
      return response;
    } catch (error) {
      console.error('MFA trigger error:', error);
      throw new Error(error.detail || error.message || 'Failed to trigger MFA');
    }
  }, []);

  /**
   * Verify MFA (check push status or verify OTP)
   */
  const verifyMFA = useCallback(
    async (mfaToken, passcode = null, setDefaultMethod = null) => {
      try {
        const response = await authService.verifyMFA(
          mfaToken,
          passcode,
          deviceFingerprintRef.current,
          rememberDeviceRef.current,
          setDefaultMethod
        );

        if (response.status === 'approved') {
          // MFA verified - complete login
          const { access_token, refresh_token, user } = response;

          // Build user object with new security schema fields
          const userWithRole = toSessionUser(user);

          setSession(access_token, userWithRole);
          localStorage.setItem('refreshToken', refresh_token);

          // See note in login() — flushSync prevents the post-MFA redirect
          // from racing the route guards.
          flushSync(() => {
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: userWithRole
              }
            });
          });

          // Start proactive token refresh — same as the password-only path.
          scheduleProactiveRefresh();

          // Cache data for offline use and sync pending entries
          handlePostLoginOfflineTasks(userWithRole);

          return { status: 'approved', message: response.message, user: userWithRole };
        }

        // Return status for pending/denied/error
        return {
          status: response.status,
          message: response.message
        };
      } catch (error) {
        console.error('MFA verify error:', error);
        throw new Error(error.detail || error.message || 'MFA verification failed');
      }
    },
    [handlePostLoginOfflineTasks]
  );

  /**
   * Initiate Duo device enrollment (returns QR code)
   */
  const enrollDuoDevice = useCallback(async (mfaToken) => {
    try {
      const response = await authService.enrollDuoDevice(mfaToken);
      return response;
    } catch (error) {
      console.error('Duo device enrollment error:', error);
      throw new Error(error.detail || error.message || 'Failed to initiate device enrollment');
    }
  }, []);

  /**
   * Check Duo enrollment status (poll after showing QR code)
   */
  const checkDuoEnrollStatus = useCallback(
    async (mfaToken) => {
      try {
        const response = await authService.checkDuoEnrollStatus(mfaToken);

        if (response.status === 'success') {
          // Enrollment complete - complete login
          const { access_token, refresh_token, user } = response;

          // Build user object with new security schema fields
          const userWithRole = toSessionUser(user);

          setSession(access_token, userWithRole);
          localStorage.setItem('refreshToken', refresh_token);

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: userWithRole
            }
          });

          // Cache data for offline use and sync pending entries
          handlePostLoginOfflineTasks(userWithRole);

          return { status: 'success', message: response.message };
        }

        // Return status for waiting/invalid/error
        return {
          status: response.status,
          message: response.message
        };
      } catch (error) {
        console.error('Duo enrollment status check error:', error);
        throw new Error(error.detail || error.message || 'Failed to check enrollment status');
      }
    },
    [handlePostLoginOfflineTasks]
  );

  /**
   * Set default MFA method for user
   */
  const setDefaultMFA = useCallback(async (mfaToken, method) => {
    try {
      const response = await authService.setDefaultMFA(mfaToken, method);
      return response;
    } catch (error) {
      console.error('Set default MFA error:', error);
      throw new Error(error.detail || error.message || 'Failed to set default MFA method');
    }
  }, []);

  const register = async (email, password, firstName, lastName) => {
    throw new Error('Registration is not yet implemented');
  };

  /* Become another user. A FULL RELOAD follows on purpose: this app caches a
   * great deal per session — menus, permissions, query results, workflow state —
   * and letting any of it survive the swap would show one user another user's
   * data. Reloading is the only way to be certain nothing carries over, and it
   * matters more here than the moment of jank costs. */
  const beginImpersonation = useCallback(async (userId) => {
    const res = await startImpersonation(userId);
    cancelProactiveRefresh();
    const parked = localStorage.getItem('refreshToken');
    if (parked) {
      localStorage.setItem(PARKED_REFRESH_KEY, parked);
      localStorage.removeItem('refreshToken');
    }
    // The TARGET's identity must replace the actor's in storage, not merely sit
    // alongside their token. setSession leaves `userData` untouched when it is
    // not given one, so passing nothing here left the admin's cached name, role
    // and organization in place — the app would greet you as yourself and draw
    // your own menu while every request went out as somebody else.
    setSession(res.access_token, res.user ? toSessionUser(res.user) : null);
    window.location.assign('/');
    return res;
  }, []);

  /* Hand the actor their own session back. The server mints the token from the
   * `act` claim, so this works even after a reload, and even in a tab that was
   * never the one that started it. */
  const endImpersonation = useCallback(async () => {
    const res = await stopImpersonation();
    // Same in reverse: without this the TARGET's cached identity would survive
    // the return journey and the actor would land back in their own account
    // wearing the other person's name and menu.
    setSession(res.access_token, res.user ? toSessionUser(res.user) : null);
    const parked = localStorage.getItem(PARKED_REFRESH_KEY);
    if (parked) {
      localStorage.setItem('refreshToken', parked);
      localStorage.removeItem(PARKED_REFRESH_KEY);
    }
    window.location.assign('/');
    return res;
  }, []);

  const logout = useCallback(async () => {
    // Stop the proactive-refresh timer first so it can't re-acquire a token
    // after we've cleared the session.
    cancelProactiveRefresh();
    // Call backend to invalidate session and record access log
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Backend logout failed:', error);
    }
    // Always clear client-side state regardless of backend result
    setSession(null);
    localStorage.removeItem('refreshToken');
    // Never leave the previous actor's refresh token for whoever signs in next.
    localStorage.removeItem(PARKED_REFRESH_KEY);
    // Clear any in-flight workflow run state so the next user doesn't see
    // stale completion ticks / active client / pre-flow selections.
    try {
      const { clearWorkflowSession } = await import('utils/workflowSession');
      clearWorkflowSession();
    } catch {
      // ignore — best-effort
    }
    // Clear offline user session from IndexedDB + React state so profile picker shows when offline
    try {
      await clearOfflineSession();
      clearActiveOfflineUser();
    } catch (error) {
      console.warn('Failed to clear offline session:', error);
    }
    dispatch({ type: LOGOUT });
  }, [clearOfflineSession, clearActiveOfflineUser]);

  const resetPassword = useCallback(async (email, recaptchaToken = null) => {
    try {
      const response = await authService.forgotPassword(email, recaptchaToken);
      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw new Error(error.detail || error.message || 'Failed to send reset email');
    }
  }, []);

  const updatePassword = useCallback(async (token, newPassword, confirmPassword) => {
    try {
      const response = await authService.resetPassword(token, newPassword, confirmPassword);
      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw new Error(error.detail || error.message || 'Failed to reset password');
    }
  }, []);

  const updateProfile = () => {};

  // Rolling (idle) session timeout. Disabled while the user is signed out,
  // and while offline — offline therapists work against IndexedDB without
  // touching the API, so logging them out mid-entry would lose unsynced work.
  const handleIdleTimeout = useCallback(async () => {
    console.warn('[Auth] Idle timeout reached — signing out.');
    try {
      await logout();
    } finally {
      // Force a hard navigation with a flag so /login can show a banner.
      window.location.assign('/login?reason=idle_timeout');
    }
  }, [logout]);

  const { showWarning, secondsRemaining, stayActive } = useIdleTimer({
    idleMinutes: IDLE_TIMEOUT_MINUTES,
    warningSeconds: IDLE_WARNING_SECONDS,
    enabled: state.isLoggedIn && isOnline,
    onTimeout: handleIdleTimeout
  });

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updatePassword,
        updateProfile,
        triggerMFA,
        verifyMFA,
        enrollDuoDevice,
        checkDuoEnrollStatus,
        setDefaultMFA,
        beginImpersonation,
        endImpersonation,
        // Derived from the token on every render, so a reload cannot lose it.
        ...readImpersonation(localStorage.getItem('serviceToken'))
      }}
    >
      {children}
      <IdleWarningDialog
        open={showWarning}
        secondsRemaining={secondsRemaining}
        warningSeconds={IDLE_WARNING_SECONDS}
        onStay={stayActive}
        onSignOut={handleIdleTimeout}
      />
    </JWTContext>
  );
};

export default JWTContext;
